import { defineStore } from 'pinia';
import type { AggregatesFragment, FilterType } from '~/types/generated-types';

export interface Argument {
  key: string;
  value: string;
}

export enum FetchStatus {
  IDLE,
  FETCHING,
  FINISHED,
  ERROR_FETCHING,
  ALL_OBJECTS_FETCHED,
  NO_RESULT,
}

interface AggregateWithRequiredProps {
  key?: string;
  displayName?: string;
  identifier?: string;
  filterType?: FilterType;
  filterUnit?: string;
  aggregates?: Aggregate[];
}

export type Aggregate = NonNullable<AggregatesFragment['aggregates']>[0] & AggregateWithRequiredProps;

interface FilterState {
  _aggregates: Aggregate[];
  _arguments: Argument[];
  _totalNumberOfHits: number;
  _defaultFilters: Argument[];
  _selectedSort: undefined | string;
  _addressFromLatLng: undefined | string;
  _fetchStatus: FetchStatus;
  _currentPage: number;
}

const _argumentBlackList: string[] = ['returnUrl'];

const initialState: () => FilterState = () => {
  return {
    _aggregates: [],
    _arguments: [],
    _totalNumberOfHits: 0,
    _defaultFilters: [],
    _selectedSort: undefined,
    _addressFromLatLng: '',
    _fetchStatus: FetchStatus.IDLE,
    _currentPage: 1,
  };
};

export const useFilterStore = defineStore('filter', {
  state: initialState,
  getters: {
    addressFromLatLng: (state: FilterState): string | undefined => state._addressFromLatLng ?? undefined,
    totalNumberOfHits: (state: FilterState): number => state._totalNumberOfHits || 0,
    arguments: (state: FilterState): Argument[] => state._arguments || [],
    aggregates: (state: FilterState): Aggregate[] => state._aggregates || [],
    defaultFilter: (state: FilterState): Argument[] => state._defaultFilters || [],
    selectedSort: (state: FilterState): string => state._selectedSort || '',
    fetchStatus: (state: FilterState): FetchStatus => state._fetchStatus,
    aggregate: (state: FilterState) => (identifier: string) => {
      return state._aggregates
        .flatMap((agg) => [agg]) // get all aggregates and sub aggregates
        .find((agg) => agg.identifier === identifier); // find the aggregate with the identifier
    },
    filter: (state: FilterState) => (identifier: string) => {
      const temp = state._arguments.find((arg) => arg.key === identifier);
      if (temp !== undefined) {
        return temp.value.split(',');
      }
    },
    currentPage: (state: FilterState) => state._currentPage,
  },
  actions: {
    setAddressFromLatLng(address: string) {
      this._addressFromLatLng = address;
    },
    changeDefaultFilters(filters: Argument[]) {
      this._defaultFilters = filters;
    },
    changeNumberOfHits(totalNumberOfHits: number) {
      this._totalNumberOfHits = totalNumberOfHits;
    },
    changeAggregates(aggregates: Aggregate[]) {
      this._aggregates = aggregates;
    },
    changeArguments(args: Argument[]) {
      this._arguments = args;
    },
    changeFetchStatus(fetchStatus: FetchStatus) {
      this._fetchStatus = fetchStatus;
    },
    changeFilter(filter: { identifier: string; values: string[] }) {
      const { identifier, values } = filter;
      if (identifier === undefined) return;
      const argument = this._arguments.find((arg) => arg.key === identifier);
      if (values.length === 0) {
        if (argument !== undefined) {
          const index = this._arguments.indexOf(argument);
          if (index > -1) {
            // delete
            this._arguments.splice(index, 1);
          }
        }
      } else if (argument !== undefined) {
        argument.value = [...new Set(values)].toString();
      } else {
        // push
        this._arguments = [
          ...this._arguments,
          {
            key: identifier,
            value: values.toString(),
          },
        ];
      }
    },
    changeCurrentPage(page: number) {
      this._currentPage = page;
    },
    changeFilters(filters: { [key: string]: string | Array<string> }) {
      Object.keys(filters).forEach((key) => {
        if (_argumentBlackList.includes(key)) delete filters[key];
      });
      this._arguments = [
        ...Object.keys(filters).map((key) => ({
          key,
          value: filters[key]?.toString() ?? '',
        })),
      ];
    },
    clearFilters() {
      this._arguments = [];
    },
    clearFilter(identifier: string) {
      this._arguments = this._arguments.filter((argument) => argument.key !== identifier);
    },
    clearRegionFilter() {
      this._arguments = this._arguments.filter((argument) => argument.key !== 'region');
    },
    changeSelectedSort(selectedSort: string) {
      this._selectedSort = selectedSort;
    },
  },
});
